/**
 * useTicketValidation Hooks
 *
 * Hooks for validating and marking tickets using URQL.
 */

import { useCallback, useState } from "react";
import { useMutation, useClient } from "urql";
import { errorTracking } from "@/lib/error-tracking";
import { extractGraphQLErrorMessage } from "@/lib/graphql-utils";
import type { TicketValidationResult } from "../../shared/types.ts";

// =============================================================================
// QUERY/MUTATION DOCUMENTS
// =============================================================================

const ValidateTicketDocument = `
  query ValidateTicket($ticketNumber: String!) {
    validateTicket(ticketNumber: $ticketNumber) {
      ticket {
        id
        ticketNumber
        status
        ticketType {
          id
          name
        }
      }
      isValid
      message
      alreadyUsedAt
      event {
        id
        title
        startsAt
      }
      customerName
    }
  }
`;

const MarkTicketUsedDocument = `
  mutation MarkTicketUsed($ticketId: ID!) {
    markTicketUsed(ticketId: $ticketId) {
      id
      status
      usedAt
    }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

interface UseValidateTicketResult {
  validateTicket: (ticketNumber: string) => Promise<TicketValidationResult>;
  isLoading: boolean;
  error: Error | null;
}

interface UseMarkTicketUsedResult {
  markTicketUsed: (ticketId: string) => Promise<void>;
  isLoading: boolean;
  error: Error | null;
}

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Validate a ticket by number.
 */
export function useValidateTicket(): UseValidateTicketResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const client = useClient();

  const validateTicket = useCallback(
    async (ticketNumber: string): Promise<TicketValidationResult> => {
      setIsLoading(true);
      setError(null);
      
      try {
        const response = await client
          .query<{ validateTicket: TicketValidationResult }>(
            ValidateTicketDocument,
            { ticketNumber: ticketNumber.trim() },
            { requestPolicy: "network-only" }
          )
          .toPromise();

        if (response.error) {
          const message = extractGraphQLErrorMessage(response.error, "Failed to validate ticket");
          throw new Error(message);
        }

        if (!response.data?.validateTicket) {
          throw new Error("No validation result returned");
        }

        errorTracking.addBreadcrumb({
          category: "admin",
          message: "Ticket validated",
          data: {
            ticketNumber,
            isValid: response.data.validateTicket.isValid,
          },
        });

        return response.data.validateTicket;
      } catch (err) {
        const validateError =
          err instanceof Error ? err : new Error("Failed to validate ticket");

        errorTracking.captureError(validateError, {
          tags: { feature: "admin", action: "validateTicket" },
        });
        setError(validateError);
        throw validateError;
      } finally {
        setIsLoading(false);
      }
    },
    [client]
  );

  return {
    validateTicket,
    isLoading,
    error,
  };
}

/**
 * Mark a ticket as used.
 */
export function useMarkTicketUsed(): UseMarkTicketUsedResult {
  const [result, executeMutation] = useMutation<
    { markTicketUsed: { id: string; status: string; usedAt: string } },
    { ticketId: string }
  >(MarkTicketUsedDocument);

  const markTicketUsed = useCallback(
    async (ticketId: string): Promise<void> => {
      try {
        const response = await executeMutation({ ticketId });

        if (response.error) {
          const message = extractGraphQLErrorMessage(response.error, "Failed to mark ticket");
          throw new Error(message);
        }

        errorTracking.addBreadcrumb({
          category: "admin",
          message: "Ticket marked as used",
          data: { ticketId },
        });
      } catch (err) {
        const markError =
          err instanceof Error ? err : new Error("Failed to mark ticket");

        errorTracking.captureError(markError, {
          tags: { feature: "admin", action: "markTicketUsed" },
        });

        throw markError;
      }
    },
    [executeMutation]
  );

  return {
    markTicketUsed,
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}
